
import { Component, Prop, Watch } from 'vue-property-decorator';
import { CreateElement } from 'vue';
import { debounce, throttle } from 'lodash';
import { mixins } from 'vue-class-component';
import PlanLimitsMixin from '@/components/ui/mixins/plan-limits-mixin';
import LoadingCircle from '@/components/global-loaders/circle.vue';

@Component({
  components: {
    LoadingCircle
  }
})
export default class UiSelect extends mixins<PlanLimitsMixin>(PlanLimitsMixin) {
  width: number = 0;

  isMenuBottom: boolean = true;
  loadingInner: boolean = false;
  activeComponent: string = 'list';
  isFocused:boolean = false;
  isBlurCalled:boolean = false;
  isSearchUpdateFrozenAfterClick:boolean = false;
  searchValue: string = '';
  fetchedItems=[]
  
  @Prop()
  items: any;

  @Prop({ default: false })
  withCustomArea: boolean;

  @Prop()
  placeholder: string;

  @Prop()
  customInfo: string;

  @Prop()
  label: string;

  @Prop()
  labelHelp: string;

  @Prop()
  name: string;

  @Prop({default:'text'})
  itemText: string;

  @Prop({default:'value'})
  itemValue: string;

  @Prop()
  itemTitle: string;

  @Prop()
  value: any;

  @Prop()
  searchInput: any;

  @Prop({ default: true })
  autocomplete: boolean;

  @Prop()
  errorMessages: string[] | string;

  @Prop()
  planLimits: keyof BillingLimits;
  
  @Prop()
  returnObject: boolean;

  @Prop()
  multiple: boolean | string;

  @Prop({default: 'default'})
  buttonStyle: string;

  @Prop()
  mini: boolean;

  @Prop()
  disabled: boolean;

  @Prop()
  prependIcon: string;

  @Prop()
  prependInnerIcon: string;

  @Prop()
  combobox: boolean | string;

  @Prop()
  chips: boolean;

  @Prop({default: () => ({})})
  menuProps: any;

  //0 -- не прячем поле ошибок
  //1 -- прячем поле ошибок
  //2 -- прячем поле ошибок, но компенсируем высоту
  @Prop()
  hideDetails: number;

  @Prop()
  maxWidthCalc: boolean | string;

  @Prop()
  dialogRepositionParam: string;

  @Prop()
  menuContentClass: string;

  @Prop()
  attach: any;

  @Prop()
  multiLine: boolean | string;

  @Prop()
  fetchOptions: any;
  
  @Prop()
  loading: any;
  
  // to fast search is value included in items list
  get itemObject(){
    if(this.fetchOptions){
      return this.items.reduce((acc, item) => {
        if(item?.[this.itemValue]){
          acc[item[this.itemValue]] = item
        }
        return acc
      }, {}) || {}
    }
    return {};
  }
  
  get calculatedItems(){
    if(this.fetchOptions && this.searchValue){
      return this.fetchedItems
    }
    
    const itemValue = this.value?.[this.itemValue]

    if(this.fetchOptions && itemValue && !this.itemObject[itemValue] ){
      return [this.value,...this.items]
    }
    return this.items
  }

  fetchOptionsSearch() {
    this.loadingInner = false;
    if(!this.searchValue) return;
    this.loadingInner = true;
    return this.fetchOptions({
      search:this.searchValue
    }).then(data=> {
      this.fetchedItems = data
      this.loadingInner = false;
    })
  }

  get fetchOptionsDebounced() {
    return debounce(this.fetchOptionsSearch, 500);
  }

  @Watch('searchValue')
  onSearchValueChanged() {
    if(this.fetchOptions && !!this.searchValue ){
      this.loadingInner = true;
      this.fetchOptionsDebounced()
    }
  }

  mounted() {
    window.addEventListener('resize', this.onWindowResizeThrottled);
    this.bus.on('dialog-scroll', this.onBusDialogScroll);

    this.$watch(
        () => {
          // @ts-ignore
          return  [ this.$refs.select.isMenuActive, this.$refs.select.virtualizedItems ];
        },
        (val) => {
          if(val) {
            this.$nextTick(() => {
              this.updateIsMenuBottom();
            })

            setTimeout(() => {
              this.updateIsMenuBottom();
            }, 500);
          }
        }
    );

    this.$watch(
        () => {
          // @ts-ignore
          return  [ this.$refs.select.isMenuActive ];
        },
        (val) => {
          if(val) {
            this.$nextTick(() => {
              this.recalcMaxWidth();
            });
          }
        }
    );
  }

  changeTab(name) {
    this.activeComponent = name
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResizeThrottled);
    this.bus.off('dialog-scroll', this.onBusDialogScroll);
  }

  updateIsMenuBottom() {
    if(!this.$refs.select) {
      return;
    }

    // @ts-ignore
    const menu = this.$refs.select.$refs.menu as any;
    const calculatedTop = parseInt(menu.calculatedTop, 10);

    if(this.isAttach  || calculatedTop === -1) {
      return;
    }

    this.isMenuBottom = menu.dimensions.activator.top < calculatedTop;
  }


  get onWindowResizeThrottled() {
    return throttle(this.onWindowResize, 500);
  }

  onBusDialogScroll(param: string) {
    if(this.dialogRepositionParam && this.dialogRepositionParam === param) {
      const select = this.$refs.select as any;
      if(select.isMenuActive) {
        select.isMenuActive = false
      }

      setTimeout(() => {
        this.updateIsMenuBottom();
      }, 300);
    }
  }

  onWindowResize() {
    this.recalcMaxWidth();
    this.$helpers.afterBrowserRedraw(() => {
      this.updateIsMenuBottom();
    });
  }

  recalcMaxWidth() {
    if(!this.isAttach && this.isMaxWidthCalc) {
      const select = this.$refs.select as UiSelect;
      const rect = select.$el.getBoundingClientRect();
      const border = 0;

      this.width = Math.floor(rect.width) - border;
    }
  }

  get component() {
    return this.combobox || this.combobox === ''
        ? 'v-combobox'
        : this.autocomplete
            ? 'v-autocomplete'
            : 'v-select';
  }

  iconPrefix: string = 'ui-select__icon';

  get pPrependIcon() {
    return this.prependIcon
        ? `${this.prependIcon} ${this.iconPrefix}`
        : this.prependIcon;
  }

  get pPrependInnerIcon() {
    return this.prependInnerIcon
        ? `${this.prependInnerIcon} ${this.iconPrefix}`
        : this.prependInnerIcon;
  }

  get styles() {
    return [ 'default', 'text' ];
  }

  get pButtonStyle() {
    return this.styles.includes(this.buttonStyle)
        ? this.buttonStyle
        : this.styles[0];
  }

  get isAttach() {
    return typeof this.attach !== 'undefined';
  }

  get isMaxWidthCalc() {
    return this.maxWidthCalc || this.maxWidthCalc === '';
  }
  
  get isMultiple(){
    return this.multiple || this.multiple === ''
  }

  genItemSlot() {
    const h = this.$createElement;

    if(!this.$scopedSlots['item'] && this.isMultiple) {
      return {
        item: (props: any) => {
          return h('ui-select-item', {
            props: {
              item: props.item,
              itemText: this.itemText,
              multiple: true
            }
          });
        }
      }
    }

    if(this.itemTitle && this.itemText) {
      return {
        item: (props: any) => {
          return h('v-list-tile-content', {
              class: 'ui-select__item--has-title',
              attrs: {
                title: props.item[this.itemTitle]
              }
            },[
              h('v-list-tile-content', props.item[this.itemText])
            ]
          );
        }
      }
    }

    if(!this.$scopedSlots['item']){
      return {
        item: (props: any) => {
          return h('ui-select-item', {
            props: {
              item: props.item,
              itemText: this.itemText,
              select: this.$refs.select
            },
          });
        }
      } 
    }
    
    return {}
  }

  get scopedSlots() {
    return {
      ...this.$scopedSlots,
      ...this.genItemSlot()
    }
  }

  genClasses(): any {
    return {
      'ui-select': true,
      'ui-select--default': this.pButtonStyle === 'default',
      'ui-select--text': this.pButtonStyle === 'text',
      'ui-select--mini': this.mini,
      'ui-select--prepend-icon': this.prependIcon || this.prependInnerIcon,
      'ui-select--without-prepend-icon': !this.prependIcon && !this.prependInnerIcon,
      'ui-select--hide-input': !this.autocomplete,
      'ui-select--controls-no-hide-details': this.hideDetails === 0,
      'ui-select--controls-hide-details': this.hideDetails > 0,
      'ui-select--controls-hide-details-compensate': this.hideDetails === 2,
      'ui-select--has-items': this.items && this.items.length,
      'ui-select--chevron': !this.$slots['append-inner'],
      'ui-select--menu-bottom': this.isMenuBottom,
      'ui-select--menu-top': !this.isMenuBottom,
      'ui-select--attach-select-top': this.attach === '',
      'ui-select--attach-select-bottom': this.attach === null,
      'ui-select--multi-line': this.multiLine || this.multiLine === '',
      'no-bwaff-fix': true,
      'ui-controll-fix': true,
      'mt-4': this.label || this.$slots['label'],
    };
  }

  get menuContentClasses() {
    let result = [
      'ui-select__menu',
      'no-bwaff-fix'
    ];

    if(this.menuContentClass){
      result.push(this.menuContentClass)
    }

    if(this.isMenuBottom) {
      result.push('ui-select__menu--bottom');
    } else {
      result.push('ui-select__menu--top');
    }

    if(!this.isAttach  && (this.multiLine || this.multiLine === '')) {
      result.push('ui-select__menu--multi-line');
    }

    return result.join(' ');
  }
  
  get computedLoading(){
    return this.loadingInner || this.loading;
  }

  genProps(): any {
    return {
      items: this.calculatedItems,
      placeholder: this.placeholder,
      itemText: this.itemText,
      itemValue: this.itemValue,
      value: this.value,
      autocomplete: this.autocomplete,
      errorMessages: this.errorMessages,
      returnObject: this.returnObject,
      appendIcon: 'far fa-angle-down',
      multiple: this.multiple,
      disabled: this.disabled,
      readonly: this.showPlanLimitsIcon,
      prependInnerIcon: this.pPrependInnerIcon,
      prependIcon: this.pPrependIcon,
      hideNoData: this.computedLoading,
      chips: this.chips,
      attach: this.attach,
      searchInput: this.searchInput,
      noFilter:!!this.fetchOptions,
      menuProps: {
        transition:false,
        ripple: false,
        bottom: true,
        offsetY: true,
        contentClass: this.menuContentClasses,
        maxWidth: this.width
            ? this.width
            : 'auto',
        ...this.menuProps
      }
    };
  }

  genSlots() {
    const h = this.$createElement;
    let result: any[] = [];
    const allowedSlots = [ 'append', 'no-data'];
    if(this.$slots) {
      allowedSlots
          .filter((slotName) => !!this.$slots[slotName])
          .forEach((slotName) => {

            const slot = h(
                'template',
                {
                  slot: slotName
                },
                [this.$slots[slotName]]
            );
            result.push(slot);
          });
    }

    if(this.computedLoading){
      result.push(h(
        'div',
        {
          slot: 'append'
        },
        [h('LoadingCircle', {
          attrs: {
            color: 'black'
          }
        })]
        )
      )
    }

    return result
  }

  genSelectWrapper() {
    const h = this.$createElement;

    return h(
        'div',
        {
          class: 'ui-select__wrapper'
        },
        [
          this.genLabel(),
          h(
              this.component,
              {
                ref: 'select',
                class: this.genClasses(),
                props: this.genProps(),
                on: {
                  input: ($event: any) => {
                    // if($event===this.value) return;
                    this.isSearchUpdateFrozenAfterClick=true;
                    setTimeout(()=>{
                      this.isSearchUpdateFrozenAfterClick=false
                    },300)
                    this.$emit('input', $event)
                  },
                  keyup: ($event: any) => {
                    this.$emit('keyup', $event);
                  },
                  focus: ($event: any) => {
                    this.isBlurCalled = false;
                    this.$emit('focus', $event);
                  },
                  blur: ($event: any) => {
                    const select = this.$refs.select as any;
                    this.isFocused = false;
                    this.$nextTick(() => {
                      if(!this.isBlurCalled){
                        select?.blur();
                        this.isBlurCalled = true;
                      }
                    });
                    if(!this.searchValue){
                      this.$nextTick(() => {
                        // select?.setValue(this.value);
                      });
                    }
                    this.fetchedItems=[];
                    this.$emit('blur', $event,this.searchValue)
                  },
                  change: ($event: any) => {
                    if(!this.isMultiple){
                      const select = this.$refs.select as any;
                      select?.blur();
                      this.isBlurCalled = true;
                    }
                    this.$emit('change', $event)
                  },
                  'update:searchInput': ($event: any) => {
                    this.$emit('update:searchInput', $event)
                    if(!$event){
                      this.searchValue = $event
                    }
                    if(!this.searchValue && this.value?.[this.itemText] === $event) return;
                    if(!this.isFocused) return;
                    if(this.isSearchUpdateFrozenAfterClick) return;
                    this.searchValue = $event;
                  },
                  mousedown: ($event: any) => {
                    if(this.showPlanLimitsIcon){return this.handleClickPlanLimits()}
                    this.isFocused = true;
                    
                    this.$emit('mousedown', $event);

                    if(!this.width) {
                      this.recalcMaxWidth();
                    }
                  }
                },
                scopedSlots: this.scopedSlots
              },
              this.genSlots()
          )
        ]
    )
  }

  genToggleButton() {
    const h = this.$createElement;

    const clickTab = name => () => this.changeTab(name)

    return h('div', { class: 'btn-group' }, [
      h('ui-button', {
        class: 'btn-list',
        props: {
          icon: 'fas fa-list',
          text: `${this.t('From List')}`,
          'button-style': this.activeComponent === 'list' ? 'accent' : 'default',
        },
        on: { click: clickTab('list')},
      }),
      h('ui-button', {
        class: 'btn-custom',
        props: {
          icon: 'fas fa-pen',
          text: `${this.t('Custom')}`,
          'button-style': this.activeComponent === 'custom' ? 'accent' : 'default',
        },
        on: { click: clickTab('custom') },
      }),
    ]);
  }

  genCustomLabel() {
    const h = this.$createElement;

    if (this.customInfo) {
      return h('ui-info', { class: 'mb-8'}, this.customInfo)
    }
    return null
  }

  changeCustomField({ target }) {
    return target.value.split('\n').filter(item => !!item)
  }

  genTextArea() {
    const h = this.$createElement;
    let textareaValue = this.value;
    if(this.returnObject){
      textareaValue = textareaValue.map((item)=>item[this.itemText])
    }

    return h('div', {}, [
      this.genCustomLabel(),
      h('v-textarea', {
        class: 'custom-field',
        props: {
          value : textareaValue.join('\n'),
          placeholder: this.placeholder,
          rows: 10,
        },
        on: {
          blur: ($event: any) => this.$emit('blur:textarea', $event, { value: this.changeCustomField($event) }),
        }
      }),
    ]);
  }

  genContent() {
    if (this.activeComponent === 'list') {
      return this.genSelectWrapper()
    }

    if (this.activeComponent === 'custom') {
      return this.genTextArea()
    }
  }

  genLabel() {
    const h = this.$createElement;

    if(this.$slots['label']) {
      return this.$slots['label'];
    }

    if(this.label) {
      return h(
          'ui-label',
          {
            props: {
              planLimits:this.planLimits,
              for: this.name,
              text: this.label,
              helpTooltip: this.labelHelp
            }
          }
      );
    }

    return null;
  }

  genSelect() {
    const h = this.$createElement;

    if(this.$slots['label']) {
      return this.$slots['label'];
    }

    if(this.label) {
      return h(
          'ui-label',
          {
            props: {
              planLimits:this.planLimits,
              for: this.name,
              text: this.label,
              helpTooltip: this.labelHelp
            }
          }
      );
    }

    return null;
  }

  blur() {
    const select = this.$refs.select as any;
    select?.blur();
  }

  render(h: CreateElement) {
    if (this.withCustomArea) {
      return h('div', {}, [
        this.genLabel(),
        this.genToggleButton(),
        this.genContent()
      ])
    }

    return  h('div', {}, [
      this.genSelectWrapper(),
    ])
  }
}
